import {
    forwardRef,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import localeData from 'dayjs/plugin/localeData';
import { useTranslation } from 'react-i18next';
// Serbian locales for date labels (same modules PointTable already loads). The
// English fallback in the renderer kicks in if a locale fails to register.
import 'dayjs/locale/sr';
import 'dayjs/locale/sr-cyrl';
import { getWindgram } from '../../services/api';
import { useForecastContext } from '../../contexts/ForecastContextProvider';
import { WindgramRenderer, DEFAULT_ALT, altitudeForVariable } from '../../lib/windgram';
import WindgramTabs from './WindgramTabs';
import AltitudeSelect from './AltitudeSelect';
import WindgramHelp from './WindgramHelp';

dayjs.extend(utc);
dayjs.extend(localeData);

// Map app i18n language codes to dayjs locale ids. Serbian Cyrillic ('src')
// maps to dayjs's 'sr-cyrl'; anything unknown falls back to English.
const DAYJS_LOCALE = { en: 'en', sr: 'sr', src: 'sr-cyrl' };

/**
 * WindgramPanel — full-day atmospheric cross-section panel.
 *
 * Reads lat/lng/date/model from URL search params (same pattern as SkewTPanel).
 * Builds 13 hourly UTC timestamps (06:00-18:00) for the selected date, fetches
 * the windgram, and mounts a WindgramRenderer into a ref'd container.
 *
 * The forwardRef exposes the outermost container DOM node so the screenshot
 * button in ForecastPage can capture it (same contract as SkewTPanel).
 */
const WindgramPanel = forwardRef(({}, ref) => {
    const { t, i18n } = useTranslation();
    const [searchParams] = useSearchParams();

    // --- URL params (mirror SkewTPanel) ---
    const lat = parseFloat(searchParams.get('lat'));
    const lng = parseFloat(searchParams.get('lng'));
    const date = searchParams.get('date') || dayjs().format('YYYY-MM-DD');
    // Model comes from the picker (context) — there is no `model` URL param;
    // reading one here meant the windgram always queried EUROPE.
    const model = (useForecastContext() || {}).model || 'EUROPE';

    // Translated strings + localized 3-day labels threaded into the renderer.
    // Keyed on language + date so switching language / day rebuilds the labels.
    const wgI18n = useMemo(() => {
        const dl = DAYJS_LOCALE[i18n.language] || 'en';
        return {
            condensationLayer: t('Condensation Layer'),
            boundaryLayer: t('Boundary Layer'),
            cloudCover: t('Cloud Cover'),
            dayLabels: [0, 1, 2].map((o) =>
                dayjs
                    .utc(date)
                    .add(o, 'day')
                    .locale(dl)
                    .format('ddd D MMM'),
            ),
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [t, i18n.language, date]);

    // --- Component state ---
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);
    const [variable, setVariable] = useState('overview');

    // ICON has no thermal-index data (ti is all zeros — verified 2026-06-11):
    // its tab is hidden, and an active ti view falls back to the overview when
    // the user switches models.
    useEffect(() => {
        if (model === 'ICON' && variable === 'ti') setVariable('overview');
    }, [model, variable]);
    const [alt, setAlt] = useState(DEFAULT_ALT);

    // --- Refs ---
    // containerRef: the full-height chart div where WindgramRenderer mounts.
    const containerRef = useRef(null);
    // rendererRef: holds the active WindgramRenderer instance.
    const rendererRef = useRef(null);
    // roRef: holds the ResizeObserver.
    const roRef = useRef(null);
    // requestId: cancellable fetch guard.
    const requestId = useRef(0);
    // wgI18nRef: latest translated strings, read by the mount effect without
    // making wgI18n a mount dependency (language changes redraw via setI18n).
    const wgI18nRef = useRef(wgI18n);
    wgI18nRef.current = wgI18n;

    // --- Build hourly timestamps for the selected date + next 2 days ---
    // 3 days × 13 steps (06:00, 07:00, ... 18:00 UTC) = 39 'YYYYMMDDHHmm' strings.
    // Mirrors Skysight's 3-day windgram canvas. The renderer tolerates fewer
    // steps if the API returns less near the forecast horizon.
    const buildHours = useCallback((dateStr) => {
        const out = [];
        for (let dayOffset = 0; dayOffset < 3; dayOffset++) {
            for (let i = 0; i < 13; i++) {
                const hour = 6 + i;
                out.push(
                    dayjs
                        .utc(dateStr)
                        .add(dayOffset, 'day')
                        .hour(hour)
                        .minute(0)
                        .second(0)
                        .format('YYYYMMDDHHmm'),
                );
            }
        }
        return out;
    }, []);

    // --- Fetch ---
    useEffect(() => {
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            setLoading(false);
            setData(null);
            return;
        }
        const id = ++requestId.current;
        setLoading(true);
        setError(null);
        const hours = buildHours(date);
        getWindgram(lat, lng, hours, model)
            .then((d) => {
                if (id !== requestId.current) return;
                setData(d);
                setLoading(false);
            })
            .catch((err) => {
                if (id !== requestId.current) return;
                setError(err);
                setLoading(false);
            });
    }, [lat, lng, date, model, retryCount, buildHours]);

    // --- Mount / update renderer ---
    useEffect(() => {
        const el = containerRef.current;
        if (!el || !data || !data.data || !data.data.length) return;

        // Destroy old renderer and observer if they exist.
        if (roRef.current) {
            roRef.current.disconnect();
            roRef.current = null;
        }

        // Instantiate fresh renderer (5th arg = i18n options object).
        const renderer = new WindgramRenderer(el, data, variable, {}, {
            i18n: wgI18nRef.current,
        });
        rendererRef.current = renderer;
        renderer.draw();

        // Wire ResizeObserver → redraw (mirror PointTableV2 pattern).
        const ro = new ResizeObserver(() => {
            rendererRef.current?.draw();
        });
        ro.observe(el);
        roRef.current = ro;

        return () => {
            if (roRef.current) {
                roRef.current.disconnect();
                roRef.current = null;
            }
        };
        // Only re-mount when data changes. variable/alt are handled via
        // the dedicated effects below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    // --- Variable change → auto-pick a meaningful altitude ceiling per tab ---
    // Skysight sets the GND–altitude per tab (thermal strength zooms to the
    // thermal top so the dome fills the chart; temp/rh/lapse/wind → 6000m;
    // overview/cloud-cover → 9000m). setAlt syncs the AltitudeSelect; the alt
    // effect below pushes it to the renderer.
    useEffect(() => {
        if (!rendererRef.current) return;
        const a = data && data.data ? altitudeForVariable(variable, data.data) : DEFAULT_ALT;
        setAlt(a);
        rendererRef.current.changeVariable(variable, a);
    }, [variable, data]);

    // --- Alt change ---
    useEffect(() => {
        if (rendererRef.current) {
            rendererRef.current.changeAlt(alt);
        }
    }, [alt]);

    // --- Language / labels change → update strings + redraw (no remount) ---
    useEffect(() => {
        if (rendererRef.current) {
            rendererRef.current.setI18n(wgI18n);
            rendererRef.current.draw();
        }
    }, [wgI18n]);

    // --- Cleanup on unmount ---
    useEffect(() => {
        return () => {
            if (roRef.current) roRef.current.disconnect();
            rendererRef.current = null;
        };
    }, []);

    // ---------------------------------------------------------------
    // Render: loading skeleton
    // ---------------------------------------------------------------
    if (loading && !data) {
        return (
            <div
                ref={ref}
                data-testid="windgram-skeleton"
                className="h-full w-full flex items-center justify-center p-4 bg-zinc-100 dark:bg-slate-800"
            >
                <img
                    src="/assets/images/logo.png"
                    className="h-12 sm:h-16 w-auto animate-pulse dark:invert dark:brightness-0"
                    alt="loading"
                />
            </div>
        );
    }

    // ---------------------------------------------------------------
    // Render: error
    // ---------------------------------------------------------------
    if (error && !data) {
        return (
            <div
                ref={ref}
                className="h-full w-full flex items-center justify-center flex-col gap-3 p-4"
            >
                <p className="text-red-600 dark:text-red-400">
                    {t("Couldn't load Point Windgram")}
                </p>
                <button
                    type="button"
                    onClick={() => setRetryCount((c) => c + 1)}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm"
                >
                    {t('Retry')}
                </button>
            </div>
        );
    }

    // ---------------------------------------------------------------
    // Render: empty / no data
    // ---------------------------------------------------------------
    if (!data || !data.data || !data.data.length) {
        return (
            <div
                ref={ref}
                className="h-full w-full flex items-center justify-center p-4 text-slate-600 dark:text-slate-400 text-center"
            >
                {t('No windgram data for this location and date.')}
            </div>
        );
    }

    // ---------------------------------------------------------------
    // Render: panel with chart
    // ---------------------------------------------------------------
    return (
        <div
            ref={ref}
            data-testid="windgram-panel"
            className="h-full w-full flex flex-col bg-white dark:bg-slate-800 overflow-hidden"
        >
            {/* Controls bar */}
            <div className="flex-none flex flex-row items-center justify-between border-b border-slate-200 dark:border-slate-700">
                <WindgramTabs value={variable} onChange={setVariable} t={t} hiddenTabs={model === 'ICON' ? ['ti'] : []} />
                <div className="px-2 flex-none">
                    <AltitudeSelect value={alt} onChange={setAlt} disabled={variable === 'overview'} />
                </div>
            </div>

            {/* Chart container — WindgramRenderer mounts into the inner div; the
                help button is a sibling overlay (top-left, by the legend) so d3
                re-renders never wipe it. */}
            <div className="flex-1 min-h-0 relative">
                <div ref={containerRef} data-testid="windgram-chart" className="w-full h-full" />
                {/* Push below the High/Med/Low cloud table, which only renders on
                    the Thermal Strength ('ti') tab (TABLE_H ≈ 82px). */}
                {/* Equal visual gap: ~16px from the top edge and ~16px from the
                    legend's left edge (legend = rightmost 25px swatch column, so
                    right = 25 + 16). Thermal Strength ('ti') sits lower to clear
                    the High/Med/Low cloud table. */}
                <div className={`absolute right-[41px] z-[1100] ${variable === 'ti' ? 'top-[100px]' : 'top-4'}`}>
                    <WindgramHelp field={variable} t={t} />
                </div>
            </div>

            {/* Spinner overlay while reloading with stale data */}
            {loading && (
                <div className="absolute top-3 right-3 w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin pointer-events-none" />
            )}
        </div>
    );
});

export default WindgramPanel;
